/**
 * formatters.js - 공통 데이터 포맷팅 유틸리티
 */

/**
 * 날짜 문자열을 'YYYY-MM-DD' 또는 'YYYY-MM-DD HH:mm:ss' 형식으로 포맷팅
 * 
 * @param {string} dateString - 날짜 문자열 (YYYYMMDD, ISO 형식 등)
 * @returns {string} 포맷팅된 날짜 또는 '-'
 */
export const formatDate = (dateString) => {
    if (!dateString || dateString === '-') return '-';

    try {
        // 이미 Date 객체인 경우
        if (dateString instanceof Date) {
            return dateString.toLocaleString('ko-KR');
        }

        // T 포함된 ISO 문자열
        if (String(dateString).includes('T')) {
            return new Date(dateString).toLocaleString('ko-KR');
        }

        // YYYYMMDD 형식 (8자리 문자열)
        if (String(dateString).length === 8 && !String(dateString).includes('-')) {
            const year = String(dateString).substring(0, 4);
            const month = String(dateString).substring(4, 6);
            const day = String(dateString).substring(6, 8);
            return `${year}-${month}-${day}`;
        }

        // 그 외 형식은 그대로 반환 (혹은 추가 파싱 로직)
        return String(dateString);
    } catch (e) {
        console.error('Date formatting error:', e);
        return dateString;
    }
};

/**
 * 숫자를 천단위 구분 콤마가 있는 문자열로 포맷팅
 * 
 * @param {number|string} amount - 포맷팅할 숫자
 * @returns {string} 포맷팅된 숫자 문자열 (예: "1,234")
 */
export const formatAmount = (amount) => {
    if (amount === undefined || amount === null || amount === '') return '0';

    const num = Number(amount);
    if (isNaN(num)) return '0';

    return new Intl.NumberFormat('ko-KR').format(num);
};

/**
 * 날짜 문자열을 'MM.DD (요일)' 형식으로 포맷팅 (출하가능일 등)
 * 
 * @param {string} dateString - 날짜 문자열
 * @returns {string} 포맷팅된 날짜 (예: "01.15 (수)")
 */
export const formatShipDate = (dateString) => {
    if (!dateString) return '';

    try {
        let date;

        if (typeof dateString === 'string') {
            if (dateString.includes('-')) {
                date = new Date(dateString);
            } else if (dateString.includes('/')) {
                date = new Date(dateString);
            } else if (dateString.length === 8) {
                const year = dateString.substring(0, 4);
                const month = dateString.substring(4, 6);
                const day = dateString.substring(6, 8);
                date = new Date(`${year}-${month}-${day}`);
            } else {
                date = new Date(dateString);
            }
        } else {
            date = new Date(dateString);
        }

        if (isNaN(date.getTime())) {
            return dateString;
        }

        const month = date.getMonth() + 1;
        const day = date.getDate();
        const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
        const weekday = weekdays[date.getDay()];

        return `${month}.${day} (${weekday})`;
    } catch (error) {
    }
};

/**
 * 전화번호 포맷팅 (010-1234-5678, 02-123-4567 등)
 * 
 * @param {string} phoneNumber - 전화번호 문자열
     * @returns {string} 포맷팅된 전화번호
     */
export const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';

    // 숫자만 추출
    const value = phoneNumber.replace(/[^0-9]/g, '');

    // 02 (서울) 지역번호인 경우
    if (value.startsWith('02')) {
        // 02-XXXX-XXXX (10자리)
        if (value.length === 10) {
            return value.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3');
        }
        // 02-XXX-XXXX (9자리)
        if (value.length === 9) {
            return value.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
        }
        // 그 외 길이가 안맞으면 그대로
        return value;
    }

    // 그 외 (010, 031 등)
    // 010-XXXX-XXXX (11자리)
    if (value.length === 11) {
        return value.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    }
    // 031-XXX-XXXX (10자리)
    if (value.length === 10) {
        return value.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }

    return value;
};
